declare const google: any;

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { environment } from '@base/environments/environment';
import { UtilsService } from 'src/app/services/utils.service';
import { Constants } from '../../models/constants';
import { LogedUser } from '../../core/models/logged-user.model';

export class GeolocationLatLng {
  lat: number = 0;
  lng: number = 0;
}
export class LocationGoogleAutocomplete {
  display_address: string = '';
  lat: number = 0;
  lng: number = 0;
  postal_code: string = '';
  street_number: string = '';
  street_name: string = '';
  neighborhood: string = '';
  city: string = '';
  city_id: string = '';
  county: string = '';
  county_id: string = '';
  state: string = '';
  state_id: string = '';
  country: string = '';
  country_id: string = '';
}
@Component({
  selector: 'app-ubication-google',
  templateUrl: './ubication-google.component.html',
  styleUrls: ['./ubication-google.component.scss'],
})
export class UbicationGoogleComponent implements OnInit {
  @Input() placeholder: string = '';
  @Input() isDisabled: boolean = false;
  @Input() isFieldAddressNeeded = false;
  @Input() isInputCleaningEnabled = true; // Limpiar input luego de seleccionar una predición
  @Input() autocompleteInput?: string | null = '';
  @Output() byChange: EventEmitter<LocationGoogleAutocomplete> =
    new EventEmitter<LocationGoogleAutocomplete>();

  predictions: any;
  searchToHighlight?: string | null = '';
  geolocationLatLng: GeolocationLatLng | null = null;
  geolocationRadiusCenter: number = 0;
  sessionToken: any;
  timer: ReturnType<typeof setTimeout> | null = null;

  readonly NODE = environment.node;
  readonly NODE_AR = Constants.TENANT_ID_AR;
  readonly NODE_EC = Constants.TENANT_ID_EC;
  readonly NODE_PY = Constants.TENANT_ID_PY;
  readonly NODE_DO = Constants.TENANT_ID_DO;
  readonly NODE_GLOBAL = Constants.TENANT_ID_GLOBAL;

  loggedUser: LogedUser | undefined;

  constructor(
    private readonly Utils: UtilsService,
    private readonly renderer: Renderer2,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.renderer.listen('window', 'click', (e: Event) => {
      // Cerra listado de predicciones al hacer click fuera
      const target: any = e.target || e.currentTarget;
      const idAttr = target ? target['attributes'].id : null;
      const value = idAttr ? idAttr.nodeValue : null;
      if (!value) {
        this.predictions = null; // Cierro predicciones
      }
      if (value !== 'field-google-autocomplete' || value !== 'predictions') {
        this.predictions = null; // Cierro predicciones
      }
    });
  }

  ngOnInit(): void {
    this.getLoggedUser();
    this.isDisabled = true;
    this.geolocationLatLng = { lat: 0, lng: 0 };
    this.geolocationRadiusCenter = 10000;
    this.checkIfGoogleLoaded();
  }

  /*
	checkIfGoogleLoaded: Verificar si se cargo correctamente el script de Google
	*/
  checkIfGoogleLoaded(): void {
    if (typeof google === 'object' && typeof google.maps === 'object') {
      this.loadGoogleResources();
      this.isDisabled = false;
    }
  }

  loadGoogleResources(): void {
    this.getGeolocation();
    this.sessionToken = new google.maps.places.AutocompleteSessionToken(); // Crear un nuevo Token de sesión
    this.isDisabled = false;
  }

  /*
	searchAddress: Se dispara al escribir en el input
	*/
  searchAddress(): void {
    if (this.autocompleteInput && this.autocompleteInput.length > 0) {
      this.getAutocompletePredictions();
    } else {
      this.byChange.emit(undefined);
    }
  }

  /*
	getAutocompletePredictions: Gestiona las prediciones del autocomplete
	*/
  private getAutocompletePredictions(): void {
    if (
      this.autocompleteInput == null ||
      this.autocompleteInput == undefined ||
      this.autocompleteInput.length < 3
    ) {
      return;
    }
    this.getPlacePredictions(this.autocompleteInput)
      .then((data) => {
        // Guarda las prediciones obtenidas
        this.predictions = data;
      })
      .catch((err) => console.log(err))
      .finally(() => {
        this.searchToHighlight = this.autocompleteInput;
      });
  }

  /*
	getPlacePredictions: Solicita las prediciones
	*/
  private getPlacePredictions(query: string): Promise<any> {
    const AUTOCOMPLETE_SERV = new google.maps.places.AutocompleteService();
    const COUNTRY = this.getCountryRestriction();

    return new Promise((resolve, reject) => {
      if (this.geolocationLatLng) {
        const CONFIG: any = {
          types: ['geocode', 'establishment'],
          input: query,
          locationBias: new google.maps.Circle({
            center: new google.maps.LatLng(
              this.geolocationLatLng.lat,
              this.geolocationLatLng.lng,
            ),
            radius: this.geolocationRadiusCenter,
          }).getBounds(),
          sessionToken: this.sessionToken,
        };

        // ✅ Solo agregar si hay país válido para reducir costos consulta
        if (COUNTRY) {
          CONFIG.componentRestrictions = { country: COUNTRY };
        }

        AUTOCOMPLETE_SERV.getPlacePredictions(
          CONFIG,
          function (predictions: any, status: any) {
            if (status == google.maps.places.PlacesServiceStatus.OK) {
              resolve(predictions);
            } else {
              reject(status);
            }
          },
        );
      }
    });
  }

  /*
	predictionSelected: Al seleccionar una predición
	*/
  predictionSelected(prediction: any): void {
    const service = new google.maps.places.PlacesService(
      document.createElement('div'),
    );

    let fields: string[] = [];
    if (this.isFieldAddressNeeded) {
      fields = ['address_component', 'geometry', 'formatted_address']; // Carga de propiedad
    } else {
      fields = ['geometry', 'formatted_address']; // ACM y BA
    }
    const request = {
      placeId: prediction.place_id,
      fields: fields,
    };

    service.getDetails(request, (place: any) => {
      if (place.formatted_address) {
        if (this.isInputCleaningEnabled) {
          this.autocompleteInput = ''; // Limpio el texto que busco el usuario del input
        } else {
          this.autocompleteInput = place.formatted_address; // En el input se muestra la dirección con formato
        }
        this.predictions = null; // Limpiar listado de prediciones

        let locationParsed: LocationGoogleAutocomplete;

        if (this.isFieldAddressNeeded) {
          locationParsed = this.Utils.parseGoogleAddress(place); // Carga de propiedad
        } else {
          locationParsed = this.Utils.parseFormattedAddressAndGeometry(place); // ACM y BA
        }

        this.byChange.emit(locationParsed);
        this.cdr.detectChanges();
      }
    });
  }

  /*
	getGeolocation: Obtener coordenas de ubicación del navegador del usuario, para influenciar
	la búsqueda con la ubicación del usuario
	*/
  getGeolocation(): boolean {
    let isGeolocationEnabled: boolean = false;
    navigator.permissions.query({ name: 'geolocation' }).then((permission) => {
      if (permission.state == 'granted') {
        isGeolocationEnabled = true;
      } else if (permission.state == 'denied') {
        isGeolocationEnabled = false;
      } else {
        isGeolocationEnabled = false;
      }
    });

    if (navigator.geolocation) {
      const _this = this; // Guardo el scope de afuera en esta variable para usarlo
      navigator.geolocation.getCurrentPosition(function (position) {
        const geolocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        _this.geolocationLatLng = geolocation;
        _this.geolocationRadiusCenter = position.coords.accuracy;
      });
    }

    return isGeolocationEnabled;
  }

  getCountryRestriction(): string | null {
    switch (this.NODE) {
      case this.NODE_AR:
        return this.loggedUser?.agency_region === 4 ? 'uy' : 'ar';
      case this.NODE_DO:
        return 'do';
      case this.NODE_PY:
        return 'py';
      case this.NODE_EC:
        return 'ec';
      default:
        return null;
    }
  }

    getLoggedUser(): void {
    this.loggedUser = this.Utils.getLoggedUser();
    }
}
